import { collection, doc, addDoc, updateDoc, deleteDoc, query, orderBy, onSnapshot, setDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";

// ─── Helpers ───
function accountPath(userId, accountId) {
  // "default" maps to the ORIGINAL path so existing user data is never lost
  if (!accountId || accountId === "default") {
    return `users/${userId}/trades`;
  }
  return `users/${userId}/accounts/${accountId}/trades`;
}

// ─── Trades ───
export async function addTrade(userId, tradeData, accountId = "default") {
  const ref = collection(db, ...accountPath(userId, accountId).split("/"));
  const docRef = await addDoc(ref, {
    ...tradeData,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });
  return { ...tradeData, id: docRef.id };
}

export async function updateTrade(userId, tradeId, tradeData, accountId = "default") {
  const ref = doc(db, ...accountPath(userId, accountId).split("/"), tradeId);
  await updateDoc(ref, {
    ...tradeData,
    updatedAt: new Date().toISOString()
  });
  return { ...tradeData, id: tradeId };
}

export async function deleteTrade(userId, tradeId, accountId = "default") {
  const ref = doc(db, ...accountPath(userId, accountId).split("/"), tradeId);
  await deleteDoc(ref);
}

export function onTradesSnapshot(userId, accountId = "default", callback) {
  const ref = collection(db, ...accountPath(userId, accountId).split("/"));
  const q = query(ref, orderBy("createdAt", "desc"));
  return onSnapshot(q, (snapshot) => {
    const trades = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(trades);
  });
}

// ─── Tags ───
export async function saveTags(userId, tags) {
  const ref = doc(db, "users", userId, "savedTags", "list");
  await setDoc(ref, { tags }, { merge: true });
}

export async function getSavedTags(userId) {
  const ref = doc(db, "users", userId, "savedTags", "list");
  const snap = await getDoc(ref);
  if (snap.exists()) return snap.data().tags || [];
  return [];
}

// ─── Rules ───
export async function saveRules(userId, rules) {
  try {
    const ref = doc(db, "users", userId, "rules", "list");
    await setDoc(ref, { rules });
  } catch (err) {
    console.error("saveRules failed:", err);
    throw err;
  }
}

export async function getRules(userId) {
  try {
    const ref = doc(db, "users", userId, "rules", "list");
    const snap = await getDoc(ref);
    if (snap.exists()) return snap.data().rules || [];
    return [];
  } catch (err) {
    console.error("getRules failed:", err);
    return [];
  }
}

export async function saveRulesHistory(userId, history) {
  try {
    const ref = doc(db, "users", userId, "rules", "history");
    await setDoc(ref, { history });
  } catch (err) {
    console.error("saveRulesHistory failed:", err);
    throw err;
  }
}

export async function getRulesHistory(userId) {
  try {
    const ref = doc(db, "users", userId, "rules", "history");
    const snap = await getDoc(ref);
    if (snap.exists()) return snap.data().history || {};
    return {};
  } catch (err) {
    console.error("getRulesHistory failed:", err);
    return {};
  }
}

// ─── Accounts ───
export async function getAccounts(userId) {
  try {
    const ref = doc(db, "users", userId, "meta", "accounts");
    const snap = await getDoc(ref);
    if (snap.exists()) return snap.data().list || null;
    return null;
  } catch (err) {
    console.error("getAccounts failed:", err);
    return null;
  }
}

export async function saveAccounts(userId, accountsList) {
  try {
    const ref = doc(db, "users", userId, "meta", "accounts");
    await setDoc(ref, { list: accountsList });
  } catch (err) {
    console.error("saveAccounts failed:", err);
    throw err;
  }
}